import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  EntityState,
} from "@reduxjs/toolkit";
import { API } from "common/utils";
import { RootState } from "model";

export type UserWithFileAccess = {
  guid: string;
  name?: string;
  mail: string;
};

class UsersWithFileAccessAPI extends API<UserWithFileAccess> {
  async getUsersWithFileAccess(
    otherUser?: string | undefined
  ): Promise<UserWithFileAccess[]> {
    const query = otherUser ? `?associatedUserId=${otherUser}` : "";
    const res = await fetch(`${this.baseUrl}${this.entity}${query}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
    });
    const userWithFileAccess = (await res.json()) as UserWithFileAccess[];
    return userWithFileAccess;
  }
}

const usersWithFileAccessApi = new UsersWithFileAccessAPI("userWithFileAccess");

const usersWithFileAccessAdapter = createEntityAdapter<UserWithFileAccess>({
  selectId: (userWithFileAccess) => userWithFileAccess.guid,
});

export const fetchUserWithFileAccess = createAsyncThunk(
  "usersWithFileAccess/fetchById",
  async (userWithFileAccessId: string) => {
    const fetchedUser = await usersWithFileAccessApi.getOne(
      userWithFileAccessId
    );
    return fetchedUser;
  }
);

export const fetchAllUsersWithFileAccess = createAsyncThunk(
  "usersWithFileAccess/fetchAll",
  async () => {
    const fetchedUsers = await usersWithFileAccessApi.getUsersWithFileAccess();
    return fetchedUsers;
  }
);

export const {
  selectAll: selectAllUsersWithFileAccess,
  selectById: selectUserWithFileAccessById,
  selectEntities: selectUsersWithFileAccessEntities,
  selectIds: selectUsersWithFileAccessIds,
} = usersWithFileAccessAdapter.getSelectors(
  (state: RootState) => state.usersWithFileAccess
);

type Status = "success" | "error" | null;
type LoadingState = "idle" | "pending";

export type UsersWithFileAccessState = EntityState<UserWithFileAccess> & {
  loading: LoadingState;
  error: null;
  status: Status;
};

const initialUsersWithFileAccessState: UsersWithFileAccessState =
  usersWithFileAccessAdapter.getInitialState({
    loading: "idle",
    error: null,
    status: null,
  });

const usersWithFileAccessSlice = createSlice({
  name: "usersWithFileAccess",
  initialState: initialUsersWithFileAccessState,
  reducers: {
    resetUsersWithFileAccess: () => initialUsersWithFileAccessState,
  },
  extraReducers: {
    [fetchUserWithFileAccess.fulfilled.type]: (state, { payload }) => {
      usersWithFileAccessAdapter.upsertOne(
        state,
        payload as UserWithFileAccess
      );
    },
    [fetchAllUsersWithFileAccess.fulfilled.type]: (state, { payload }) => {
      usersWithFileAccessAdapter.addMany(
        state,
        payload as UserWithFileAccess[]
      );
    },
  },
});

const { actions, reducer } = usersWithFileAccessSlice;

export const { resetUsersWithFileAccess } = actions;
export const usersWithFileAccessReducer = reducer;
